import React, { useContext } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Provider } from "react-redux";
import store from "./store";
import FormEditorPage from "./pages/FormEditorPage";
import FormFillPage from "./pages/FormFillPage";
import ThankYouPage from "./pages/ThankYouPage";
import ThankYouMarkelPage from "./pages/ThankYouMarkelPage";
import ContractVersion from "./pages/ContractVersionPage";
import { Toaster } from "react-hot-toast";
import { AuthProvider, AuthContext } from "./context/AuthContext";
import { BackgroundColorProvider } from "./context/BackgroundColorContext";
import AuthHandler from "./components/AuthHandler";
import Header from "./components/Header";
import LoginPage from "./pages/LoginPage";
import FormDownloadPage from "./pages/FormDownloadPage";
import FormFillDownloadPage from "./pages/FormFillDownloadPage";
import ContractDownloadPage from "./pages/ContractDownloadPage";
import TemplateLibrary from "./pages/TemplateLibraryPage";
import CreateTemplateLibrary from "./pages/CreateTemplateLibrary";
import ClientFormFillPage from "./pages/ClientFormFillPage";
import MarkelContractDocument from "./pages/MarkelContractDocumentPage";
import ContractDocumentApproveReject from "./pages/ContractDocumentApproveReject";
import DocumentPage from "./pages/DocumentPage";
import ClientPage from "./pages/ClientViewPage";
import NoAccessPage from "./pages/NoAccessPage";
import { ROLE } from "./utils/commonConstant";
import AlreadySignedPage from "./pages/AlreadySignedPage";
import { HighlightProvider } from "./context/HighlightContext";
import RejectInfoPage from "./pages/RejectPage";
import ProjectTourPage from "./pages/ProjectTourPage";
import CreateRegistrationFormPage from "./pages/CreateRegistrationForm";

const PrivateRoute = ({ element, allowedRoles }) => {
  const { auth } = useContext(AuthContext);
  const location = useLocation();
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const token = urlParams.get('access_token');
  if (token) {
    if (urlParams.get('tempCode')) {
      localStorage.setItem("TempCode", urlParams.get('tempCode'));
      localStorage.setItem("Token", token);
      localStorage.setItem("Role", urlParams.get('role'));
      localStorage.setItem("UserId", urlParams.get('userId'));
    }
    auth.Token = token;
    auth.Email = urlParams.get('email');
    auth.Name = urlParams.get('name');
    auth.Role = urlParams.get('role');
    auth.TokenExpiry = urlParams.get('tokenExpiry');
    auth.UserId = urlParams.get('userId');
  }
  if (!auth.Token) {
    let currentUrl = encodeURIComponent(window.location.href);
    const baseUrl = window.location.origin;
    const redirectUrl =
      location.pathname === "/"
        ? `${baseUrl}/auth-callback?redirect=${currentUrl}home`
        : `${baseUrl}/auth-callback?redirect=${currentUrl}`;
    window.location.href = `${process.env.REACT_APP_BASE_API_URL
      }/Auth/HHPortal?hhredirect=${encodeURIComponent(redirectUrl)}`;
    return null;
  }

  if (!allowedRoles.includes(auth.Role)) {
    return <Navigate to="/no-access" state={{ from: location }} replace />;
  }

  return (
    <>
      <Header />
      {element}
    </>
  );
};

function App() {
  return (
    <Provider store={store}>
      <AuthProvider>
        <BackgroundColorProvider>
          <HighlightProvider>
            <Toaster position="top-right" />
            <Router>
              <Routes>
                <Route path="/auth-callback" element={<AuthHandler />} />
                <Route path="/login" element={<LoginPage />} />
                <Route
                  path="/:clientId/form/:documentType/:subcotractorTypeId/:subcontractorId/:notificationScheduleId"
                  element={<FormFillPage />}
                />
                <Route
                  path="/:clientId/:hhDocumentId/:hhTemplateId"
                  element={
                    <PrivateRoute
                      element={<ContractVersion />}
                      allowedRoles={[ROLE.ADMIN, ROLE.MANAGER, ROLE.DEV]}
                    />
                  }
                />
                <Route
                  path="/:clientId"
                  element={
                    <PrivateRoute
                      element={<DocumentPage />}
                      allowedRoles={[ROLE.ADMIN, ROLE.MANAGER, ROLE.MARKEL, ROLE.DEV]}
                    />
                  }
                />
                <Route
                  path="/:clientId/:hhDocumentId/versions/:templateId/:action/:contractVersionId?"
                  element={
                    <PrivateRoute
                      element={<FormEditorPage />}
                      allowedRoles={[ROLE.ADMIN, ROLE.MANAGER, ROLE.DEV]}
                    />
                  }
                />
                <Route path="/thank-you" element={<ThankYouPage />} />
                <Route path="/thank-you-markel" element={<ThankYouMarkelPage />} />
                <Route
                  path="/:clientId/download/:contractVersionId"
                  element={<FormDownloadPage />}
                />
                <Route
                  path="/:clientId/generate-pdf/:fileId"
                  element={<FormFillDownloadPage />}
                />
                <Route
                  path="/:clientId/generate-unsigned-pdf/:fileId"
                  element={<ContractDownloadPage />}
                />
                <Route
                  path="/:clientId/template"
                  element={
                    <PrivateRoute
                      element={<TemplateLibrary />}
                      allowedRoles={[ROLE.ADMIN, ROLE.MANAGER, ROLE.DEV]}
                    />
                  }
                />
                <Route
                  path="/:clientId/template/:action/:templateId?"
                  element={
                    <PrivateRoute
                      element={<CreateTemplateLibrary />}
                      allowedRoles={[ROLE.ADMIN, ROLE.MANAGER, ROLE.DEV]}
                    />
                  }
                />
                <Route
                  path="/client-sign/:clientId/:contractVersionId"
                  element={<ClientFormFillPage />}
                />
                <Route
                  path="/tutorial"
                  element={<ProjectTourPage />}
                />
                <Route
                  path="/contract/:clientId/:hhDocumentId"
                  element={
                    <PrivateRoute
                      element={<MarkelContractDocument />}
                      allowedRoles={[ROLE.MARKEL, ROLE.ADMIN, ROLE.MANAGER, ROLE.DEV]}
                    />
                  }
                />
                <Route
                  path="/contract-document/:clientId/:contractVersionId"
                  element={
                    <PrivateRoute
                      element={<ContractDocumentApproveReject />}
                      allowedRoles={[ROLE.MARKEL, ROLE.ADMIN, ROLE.MANAGER, ROLE.DEV]}
                    />
                  }
                />
                <Route
                  path="/home"
                  element={
                    <PrivateRoute
                      element={<ClientPage />}
                      allowedRoles={[ROLE.ADMIN, ROLE.MANAGER, ROLE.MARKEL, ROLE.DEV]}
                    />
                  }
                />
                <Route
                  path="/"
                  element={
                    <PrivateRoute
                      element={<ClientPage />}
                      allowedRoles={[ROLE.ADMIN, ROLE.MANAGER, ROLE.MARKEL, ROLE.DEV]}
                    />
                  }
                />
                <Route path="/no-access" element={<NoAccessPage />} />
                <Route path="*" element={<Navigate to="/not-found" />} />
                <Route path="/already-signed" element={<AlreadySignedPage />} />
                <Route path="/rejected" element={<RejectInfoPage />} />
                <Route path="/registration" element={<CreateRegistrationFormPage />} />
              </Routes>
            </Router>
          </HighlightProvider>
        </BackgroundColorProvider>
      </AuthProvider>
    </Provider>
  );
}

export default App;
